import React, { createContext, useContext, useRef } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  key: string;
}

interface DataCacheContextType {
  getCachedData: <T>(key: string) => T | null;
  setCachedData: <T>(key: string, data: T) => void;
  isCacheValid: (key: string, maxAge?: number) => boolean;
  clearCache: (key?: string) => void;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

export const DataCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Map<string, CacheEntry<any>>>(new Map());

  const getCachedData = <T,>(key: string): T | null => {
    const entry = cacheRef.current.get(key);
    return entry ? entry.data : null;
  };

  const setCachedData = <T,>(key: string, data: T) => {
    cacheRef.current.set(key, {
      data,
      timestamp: Date.now(),
      key,
    });
  };

  const isCacheValid = (key: string, maxAge: number = 5 * 60 * 1000): boolean => {
    const entry = cacheRef.current.get(key);
    if (!entry) return false;
    
    const age = Date.now() - entry.timestamp;
    return age < maxAge;
  };

  const clearCache = (key?: string) => {
    if (key) {
      cacheRef.current.delete(key);
    } else {
      cacheRef.current.clear();
    }
  };

  const value = {
    getCachedData,
    setCachedData,
    isCacheValid,
    clearCache,
  };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (context === undefined) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
};
